"use client"

import { Check, CircleHelp, Copy, Signal } from "lucide-react"
import { useState } from "react"

import { accountText, type LocaleProps } from "../../personal-web"
import type { DeviceFlow } from "../data/steps"

type Props = LocaleProps & {
  flow: DeviceFlow
  /**
   * The plan-specific APN string from the eSIM record (variant metadata).
   * Empty string when used on the public guide page (no eSIM context) —
   * we then suppress the "manual fallback" line entirely.
   */
  variantApn?: string
}

/**
 * Network info callout under the Connect steps — surfaces APN + data roaming
 * as a quick reference. Matches the Airalo PDF sidebar layout.
 *
 * APN handling: both iOS and Android auto-configure APN from the eSIM
 * profile in nearly every case, so "Set automatically" is the primary
 * value. The plan's actual APN (variantApn) is shown underneath only as a
 * fallback for the rare auto-detect failure — and that fallback is wrapped
 * in a click-to-copy pill so customers who do hit the manual path can paste
 * it directly into Settings without retyping.
 *
 * Used by:
 *   - account/esim/{id}/installation (with variantApn from esim metadata)
 *   - /installation-guide (no variantApn — fallback line is hidden)
 */
export function NetworkInfoBox({ lng, flow, variantApn = "" }: Props) {
  const apnPrimary = accountText(lng, "install.ios.network.apnAuto")
  // Use a unique sentinel for the slot so we can split the localized string
  // around it and inject a styled pill. The slot string is something a real
  // translation can never produce by accident.
  const APN_SLOT = "__APN_SLOT__"
  const apnHintRaw = variantApn
    ? accountText(lng, "install.network.apnManualHint", { apn: APN_SLOT })
    : ""
  const [apnHintBefore, apnHintAfter] = apnHintRaw
    ? (apnHintRaw.split(APN_SLOT).concat([""]) as [string, string])
    : (["", ""] as [string, string])
  return (
    <aside className="grid gap-2 rounded-2xl border border-stone-200 bg-stone-50/50 p-4 text-sm md:grid-cols-2 md:p-5">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-200/70 text-stone-700"
        >
          <CircleHelp className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
            {accountText(lng, "install.network.apnLabel")}
          </p>
          <p className="text-[13px] font-semibold text-slate-900">
            {apnPrimary}
          </p>
          {variantApn ? (
            <p className="mt-1 text-[11px] leading-snug text-stone-500">
              {apnHintBefore}
              <ApnCopyPill lng={lng} value={variantApn} />
              {apnHintAfter}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-200/70 text-stone-700"
        >
          <Signal className="h-4 w-4" />
        </span>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
            {accountText(lng, "install.network.roamingLabel")}
          </p>
          <p className="text-[13px] font-semibold text-slate-900">
            {accountText(
              lng,
              flow.networkInfo.roaming === "on"
                ? "install.network.roamingOn"
                : "install.network.roamingOff"
            )}
          </p>
        </div>
      </div>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Inline click-to-copy pill. Used to surface the plan's APN value inside
// the manual-fallback hint so customers can paste it straight into Settings
// without retyping. Falls back to the document.execCommand path when
// navigator.clipboard isn't available (older WebKit, http origins).
// ─────────────────────────────────────────────────────────────────────────

function ApnCopyPill({ lng, value }: LocaleProps & { value: string }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard?.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = value
      ta.style.position = "fixed"
      ta.style.left = "-9999px"
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand("copy")
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1800)
      } catch {
        // Give up silently — long-press still works on mobile.
      }
      document.body.removeChild(ta)
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-live="polite"
      aria-label={accountText(lng, "install.network.apnCopyAriaLabel", { apn: value })}
      className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 align-baseline font-mono text-[11px] font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-orange-500 ${
        copied
          ? "bg-emerald-100 text-emerald-700"
          : "bg-orange-100 text-orange-700 hover:bg-orange-200"
      }`}
    >
      <span>{value}</span>
      {copied ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <Copy className="h-3 w-3" aria-hidden />
      )}
    </button>
  )
}
